<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'
import { DatePicker, parseDate } from '@ark-ui/vue'
import type { DateValue } from '@ark-ui/vue'
import UiFieldContainer from './UiFieldContainer.vue'
import UiIcon from './UiIcon.vue'
import { Calendar } from 'lucide-vue-next'

export interface UiDatePickerProps {
    name?: string
    id?: string
    placeholder?: string
    disabled?: boolean
    size?: 'md' | 'lg'
    hint?: string
    error?: string
    invalid?: boolean
    range?: boolean
    min?: DateValue
    max?: DateValue
    locale?: string
    format?: string
    defaultView?: 'day' | 'month' | 'year'
    minView?: 'day' | 'month' | 'year'
    maxView?: 'day' | 'month' | 'year'
}

const props = withDefaults(defineProps<UiDatePickerProps>(), {
    size: 'md',
    range: false,
    locale: 'en-US',
    defaultView: 'day',
    minView: 'day',
    maxView: 'year',
})

const model = defineModel<DateValue | DateValue[] | string | string[]>()

const emit = defineEmits<{
    (e: 'validate'): void
}>()

const isOpen = ref(false)
const isFocused = ref(false)

const describedByBase = computed(() => props.id || props.name)

const internalValue = ref<DateValue[]>([])

function formatDate(date: any): string {
    if (!date) return ''
    const jsDate = new Date(date.year, date.month - 1, date.day)
    
    // Format based on minView (selection granularity)
    if (props.minView === 'month') {
        return jsDate.toLocaleDateString(props.locale, {
            year: 'numeric',
            month: 'short'
        })
    } else if (props.minView === 'year') {
        return jsDate.toLocaleDateString(props.locale, {
            year: 'numeric'
        })
    }
    
    return jsDate.toLocaleDateString(props.locale, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    })
}

const triggerLabel = computed(() => {
    if (!internalValue.value || internalValue.value.length === 0) {
        return props.placeholder || (props.range ? 'Select date range...' : 'Select date...')
    }
    
    if (props.range && internalValue.value.length === 2) {
        return `${formatDate(internalValue.value[0])} - ${formatDate(internalValue.value[1])}`
    }
    
    return formatDate(internalValue.value[0])
})

function getDateValue(val: string | DateValue): DateValue | null {
    if (!val) return null
    try {
        if (typeof val === 'string') {
            return parseDate(val)
        }
        return val
    } catch (error) {
        console.warn('Failed to parse date value:', val, error)
        return null
    }
}

const isUpdatingFromModel = ref(false)
const isUpdatingFromInternal = ref(false)

watch(model, (newVal) => {
    if (isUpdatingFromInternal.value) return
    
    isUpdatingFromModel.value = true
    
    if (!newVal) {
        internalValue.value = []
        isUpdatingFromModel.value = false
        return
    }
    
    if (Array.isArray(newVal) && newVal.some(val => typeof val === 'string')) {
        const parsed = newVal.map(val => getDateValue(val)).filter(v => v !== null) as DateValue[]
        internalValue.value = parsed
    } else if (typeof newVal === 'string') {
        const parsed = getDateValue(newVal)
        internalValue.value = parsed ? [parsed] : []
    } else {
        internalValue.value = Array.isArray(newVal) ? newVal as DateValue[] : [newVal as DateValue]
    }
    
    isUpdatingFromModel.value = false
}, { immediate: true, deep: true })

watch(internalValue, (val) => {
    if (isUpdatingFromModel.value) return
    
    isUpdatingFromInternal.value = true
    
    if (props.range) {
        model.value = val as any
    } else {
        model.value = val[0] as any
    }
    
    emit('validate')
    isUpdatingFromInternal.value = false
}, { deep: true })

// Force repositioning when dropdown opens to fix SSR positioning issues
// Use requestAnimationFrame after nextTick to ensure lazy-mounted content is rendered
watch(isOpen, (newVal) => {
    if (newVal && typeof window !== 'undefined') {
        nextTick(() => {
            requestAnimationFrame(() => {
                window.dispatchEvent(new Event('resize'))
            })
        })
    }
})

export type { DateValue }
</script>

<template>
    <UiFieldContainer 
        :size="props.size" 
        :invalid="props.invalid" 
        :hint="props.hint" 
        :error="props.error"
        :describedByIdBase="describedByBase" 
        :isFocused="isFocused || isOpen"
    >
        <DatePicker.Root 
            class="w-full" 
            v-model="internalValue as DateValue[]" 
            :selectionMode="props.range ? 'range' : 'single'"
            :disabled="props.disabled"
            :id="props.id || props.name"
            :name="props.name"
            v-model:open="isOpen"
            :min="props.min"
            :max="props.max"
            :locale="props.locale"
            :closeOnSelect="!props.range"
            :defaultView="props.defaultView"
            :minView="props.minView"
            :maxView="props.maxView"
            lazyMount
        >
            <DatePicker.Control class="w-full">
                <DatePicker.Trigger class="w-full flex items-center justify-between cursor-pointer">
                    <span class="text-left truncate w-full">
                        {{ triggerLabel }}
                    </span>
                    <Calendar class="w-4 h-4 text-se-gray-700" />
                </DatePicker.Trigger>
            </DatePicker.Control>
            
            <Teleport to="body" defer>
                <DatePicker.Positioner>
                    <DatePicker.Content
                        class="border-1 border-se-gray-300 rounded-lg shadow-lg bg-se-white my-2 p-4 w-max z-100 min-w-[300px]"
                    >
                        <!-- Day View -->
                        <DatePicker.View view="day">
                            <DatePicker.Context v-slot="api">
                                <div class="flex flex-col gap-3">
                                    <!-- Month/Year Controls -->
                                    <div class="flex items-center justify-between mb-2">
                                        <DatePicker.PrevTrigger 
                                            class="p-2 hover:bg-se-gray-100 rounded-md cursor-pointer"
                                        >
                                            <UiIcon name="chevron-down" class="w-4 h-4 rotate-90 text-se-gray-700" />
                                        </DatePicker.PrevTrigger>
                                        
                                        <DatePicker.ViewTrigger class="font-medium text-se-gray-900 hover:bg-se-gray-100 px-3 py-1 rounded-md cursor-pointer">
                                            <DatePicker.RangeText  />
                                        </DatePicker.ViewTrigger>
                                        
                                        <DatePicker.NextTrigger 
                                            class="p-2 hover:bg-se-gray-100 rounded-md cursor-pointer"
                                        >
                                            <UiIcon name="chevron-down" class="w-4 h-4 -rotate-90 text-se-gray-700" />
                                        </DatePicker.NextTrigger>
                                    </div>

                                    <!-- Calendar Table -->
                                    <DatePicker.Table class="w-full border-collapse">
                                        <DatePicker.TableHead>
                                            <DatePicker.TableRow>
                                                <DatePicker.TableHeader 
                                                    v-for="(weekDay, id) in api.weekDays" 
                                                    :key="id"
                                                    class="text-xs font-medium text-se-gray-600 pb-2 text-center w-10"
                                                >
                                                    {{ weekDay.short }}
                                                </DatePicker.TableHeader>
                                            </DatePicker.TableRow>
                                        </DatePicker.TableHead>
                                        <DatePicker.TableBody>
                                            <DatePicker.TableRow v-for="(week, id) in api.weeks" :key="id">
                                                <DatePicker.TableCell 
                                                    v-for="(day, id) in week" 
                                                    :key="id" 
                                                    :value="day"
                                                    class="w-10 h-10 p-0"
                                                >
                                                    <DatePicker.TableCellTrigger 
                                                        class="w-full h-full flex items-center justify-center rounded-md text-sm hover:bg-se-gray-100 cursor-pointer data-[selected]:bg-se-dark data-[selected]:text-se-white data-[today]:border data-[today]:border-se-dark data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed data-[outside-range]:text-se-gray-400 data-[in-range]:bg-se-dark/10"
                                                    >
                                                        {{ day.day }}
                                                    </DatePicker.TableCellTrigger>
                                                </DatePicker.TableCell>
                                            </DatePicker.TableRow>
                                        </DatePicker.TableBody>
                                    </DatePicker.Table>
                                </div>
                            </DatePicker.Context>
                        </DatePicker.View>

                        <!-- Month View -->
                        <DatePicker.View view="month">
                            <DatePicker.Context v-slot="api">
                                <div class="flex flex-col gap-3">
                                    <!-- Year Controls -->
                                    <div class="flex items-center justify-between mb-2">
                                        <DatePicker.PrevTrigger 
                                            class="p-2 hover:bg-se-gray-100 rounded-md cursor-pointer"
                                        >
                                            <UiIcon name="chevron-down" class="w-4 h-4 rotate-90 text-se-gray-700" />
                                        </DatePicker.PrevTrigger>
                                        
                                        <DatePicker.ViewTrigger class="font-medium text-se-gray-900 hover:bg-se-gray-100 px-3 py-1 rounded-md cursor-pointer">
                                            <DatePicker.RangeText />
                                        </DatePicker.ViewTrigger>
                                        
                                        <DatePicker.NextTrigger 
                                            class="p-2 hover:bg-se-gray-100 rounded-md cursor-pointer"
                                        >
                                            <UiIcon name="chevron-down" class="w-4 h-4 -rotate-90 text-se-gray-700" />
                                        </DatePicker.NextTrigger>
                                    </div>

                                    <!-- Month Grid -->
                                    <DatePicker.Table>
                                        <DatePicker.TableBody>
                                            <DatePicker.TableRow
                                                v-for="(months, id) in api.getMonthsGrid({ columns: 4, format: 'short' })"
                                                :key="id"
                                                class="flex gap-2 mb-2"
                                            >
                                                <DatePicker.TableCell 
                                                    v-for="(month, id) in months" 
                                                    :key="id" 
                                                    :value="month.value"
                                                    class="flex-1"
                                                >
                                                    <DatePicker.TableCellTrigger 
                                                        class="w-full h-10 flex items-center justify-center rounded-md text-sm hover:bg-se-gray-100 cursor-pointer data-[selected]:bg-se-dark data-[selected]:text-se-white data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed data-[in-range]:bg-se-dark/10"
                                                    >
                                                        {{ month.label }}
                                                    </DatePicker.TableCellTrigger>
                                                </DatePicker.TableCell>
                                            </DatePicker.TableRow>
                                        </DatePicker.TableBody>
                                    </DatePicker.Table>
                                </div>
                            </DatePicker.Context>
                        </DatePicker.View>

                        <!-- Year View -->
                        <DatePicker.View view="year">
                            <DatePicker.Context v-slot="api">
                                <div class="flex flex-col gap-3">
                                    <!-- Decade Controls -->
                                    <div class="flex items-center justify-between mb-2">
                                        <DatePicker.PrevTrigger 
                                            class="p-2 hover:bg-se-gray-100 rounded-md cursor-pointer"
                                        >
                                            <UiIcon name="chevron-down" class="w-4 h-4 rotate-90 text-se-gray-700" />
                                        </DatePicker.PrevTrigger>
                                        
                                        <DatePicker.ViewTrigger class="font-medium text-se-gray-900 hover:bg-se-gray-100 px-3 py-1 rounded-md cursor-pointer">
                                            <DatePicker.RangeText />
                                        </DatePicker.ViewTrigger>
                                        
                                        <DatePicker.NextTrigger 
                                            class="p-2 hover:bg-se-gray-100 rounded-md cursor-pointer"
                                        >
                                            <UiIcon name="chevron-down" class="w-4 h-4 -rotate-90 text-se-gray-700" />
                                        </DatePicker.NextTrigger>
                                    </div>

                                    <!-- Year Grid -->
                                    <DatePicker.Table>
                                        <DatePicker.TableBody>
                                            <DatePicker.TableRow 
                                                v-for="(years, id) in api.getYearsGrid({ columns: 4 })" 
                                                :key="id"
                                                class="flex gap-2 mb-2"
                                            >
                                                <DatePicker.TableCell 
                                                    v-for="(year, id) in years" 
                                                    :key="id" 
                                                    :value="year.value"
                                                    class="flex-1"
                                                >
                                                    <DatePicker.TableCellTrigger 
                                                        class="w-full h-10 flex items-center justify-center rounded-md text-sm hover:bg-se-gray-100 cursor-pointer data-[selected]:bg-se-dark data-[selected]:text-se-white data-[disabled]:opacity-40 data-[disabled]:cursor-not-allowed data-[in-range]:bg-se-dark/10"
                                                    >
                                                        {{ year.label }}
                                                    </DatePicker.TableCellTrigger>
                                                </DatePicker.TableCell>
                                            </DatePicker.TableRow>
                                        </DatePicker.TableBody>
                                    </DatePicker.Table>
                                </div>
                            </DatePicker.Context>
                        </DatePicker.View>
                    </DatePicker.Content>
                </DatePicker.Positioner>
            </Teleport>
        </DatePicker.Root>
    </UiFieldContainer>
</template>
